<!-- Toolbar: search panel + group panel + column chooser + export.
Replaces onToolbarPreparing / dxo-search-panel / dxo-group-panel /
dxo-column-chooser / dxo-export. -->
@if (searchPanel || (groupPanel && groupFields.length) || columnChooser ||
allowExport || toolbarItems.length) {
<div class="agrid-toolbar">
  <div class="agrid-toolbar-left">
    @if (searchPanel) {
    <!-- The magnifier is PROJECTED into the [nzInputPrefix] slot, not handed
         over as a TemplateRef. ng-zorro v22 types nzPrefix as a string and
         renders it with {{ nzPrefix() }}, so a <ng-template> ref stringified
         to the literal "[object Object]" in front of the placeholder. -->
    <nz-input-wrapper class="agrid-search">
      <span nzInputPrefix nz-icon nzType="search"></span>
      <input
        nz-input
        [ngModel]="searchText"
        (ngModelChange)="onSearch($event)"
        placeholder="Search..."
        aria-label="Search grid"
      />
    </nz-input-wrapper>
    }
    <!-- Group panel.
        The "Group by" SELECT that used to sit here has been removed at the
        user's request: a dropdown listing every column was intrusive on screens
        that are already grouped by configuration, and it is not what DevExtreme
        showed (that was a drop target, not a picker).
        Grouping itself is unchanged. Columns with a groupIndex still group on
        load, group headers and counts still render, and group footers still
        total. What is gone is only the control for changing the grouping by
        hand; the chips below still show what a grid is grouped by, and still
        remove it.
        Rendered only when something IS grouped, so a grid with no grouping now
        shows nothing here at all. -->
    @if (groupPanel && groupFields.length) {
    <div class="agrid-grouppanel">
      <span class="agrid-grouppanel-label">Grouped by:</span>
      @for (g of groupFields; track g) {
      <nz-tag nzMode="closeable" (nzOnClose)="removeGroup(g)">
        {{ captionOf(g) }}
      </nz-tag>
      }
    </div>
    }
  </div>
  <div class="agrid-toolbar-right">
    @for (item of toolbarItems; track item) {
    <button
      nz-button
      nzType="default"
      [disabled]="item.disabled"
      [ngClass]="item.cssClass"
      [hidden]="item.visible === false"
      nz-tooltip
      [nzTooltipTitle]="item.hint"
      (click)="item.onClick()"
    >
      @if (item.icon) {
      <span nz-icon [nzType]="item.icon"></span>
      }{{ item.text }}
    </button>
    } @if (columnChooser) {
    <button
      nz-button
      nz-dropdown
      [nzDropdownMenu]="chooserMenu"
      nzTrigger="click"
      nz-tooltip
      nzTooltipTitle="Choose columns"
    >
      <span nz-icon nzType="setting"></span>
    </button>
    }
    <nz-dropdown-menu #chooserMenu="nzDropdownMenu">
      <div class="agrid-chooser">
        @for (c of chooserColumns; track c) {
        <label
          nz-checkbox
          [ngModel]="c.visible !== false"
          (ngModelChange)="toggleColumn(c)"
        >
          {{ c.caption || c.dataField }}
        </label>
        }
      </div>
    </nz-dropdown-menu>
    @if (allowExport) {
    <button
      nz-button
      nzType="default"
      (click)="exportExcel()"
      nz-tooltip
      nzTooltipTitle="Export to Excel"
    >
      <span nz-icon nzType="file-excel"></span>
    </button>
    }
  </div>
</div>
}

<!-- Filter panel (dxo-filter-panel): DevExtreme showed the active filter
    expression with a Clear link. Chips carry the same information and let a
    single condition be dropped without clearing the rest. -->
@if (filterPanel && activeFilters.length) {
<div class="agrid-filterpanel">
  <span class="agrid-filterpanel-label">Filters:</span>
  @for (f of activeFilters; track f) {
  <nz-tag nzMode="closeable" (nzOnClose)="clearFilter(f.field)">
    {{ f.label }}
  </nz-tag>
  }
  <a class="agrid-clearall" (click)="clearAllFilters()">Clear all</a>
</div>
}

<nz-table
  #table
  [nzData]="view"
  [nzFrontPagination]="false"
  [nzShowPagination]="pagingEnabled"
  [nzTotal]="total"
  [nzPageIndex]="pageIndex"
  [nzPageSize]="pageSize"
  [nzShowSizeChanger]="showPageSizeSelector"
  [nzPageSizeOptions]="allowedPageSizes"
  [nzShowTotal]="totalTpl"
  [nzLoading]="loading"
  [nzScroll]="scrollX ? { x: scrollX } : {}"
  [nzBordered]="showBorders"
  nzSize="small"
  [nzNoResult]="noDataText"
  (nzPageIndexChange)="onPageIndex($event)"
  (nzPageSizeChange)="onPageSize($event)"
  class="agrid-table"
  [class.agrid-striped]="rowAlternationEnabled"
  [class.agrid-nowrap]="!wordWrapEnabled"
>
  <!-- nzShowTotal's context is { $implicit: total, range }, so the total is the
      IMPLICIT value - reading it as let-t="total" left it undefined and every
      grid in the app rendered "1-6 of " with the count missing. -->
  <ng-template #totalTpl let-t let-range="range"
    >{{ range[0] }}-{{ range[1] }} of {{ t }}</ng-template
  >

  <thead>
    <!-- HEADER. One <tr> per level of the column tree: a grid with no banded
        columns has depth 1 and renders the single row it always did, and a
        banded one renders as many rows as it nests deep. A band spans its
        leaves with colspan; a leaf declared above the bottom level spans down
        to it with rowspan, so a header mixing banded and plain columns has no
        holes under the plain ones.

        The leaf <th> is ONE template used at every level. It was previously
        written twice - once for the banded header and once for the plain one -
        and the two had already drifted: the banded copy had lost header
        filters, resizers and headerCellTemplate support. -->
    @for (row of headerRows; track row; let level = $index) {
    <tr>
      <!-- Row-leading cells belong to the whole header, so they are emitted on
            the first row and span all of it. -->
      @if (level === 0) {
      <!-- master-detail expander (dxo-master-detail) -->
      @if (masterDetail) {
      <th
        class="agrid-narrow"
        [attr.rowspan]="headerRows.length > 1 ? headerRows.length : null"
      ></th>
      }
      <!-- selection (dxo-selection) -->
      @if (selectionMode !== 'none') {
      <th
        class="agrid-narrow"
        [attr.rowspan]="headerRows.length > 1 ? headerRows.length : null"
      >
        @if (selectionMode === 'multiple') {
        <label
          nz-checkbox
          [ngModel]="allOnPageSelected"
          (ngModelChange)="toggleAllOnPage($event)"
          aria-label="Select all rows on this page"
        ></label>
        }
      </th>
      } } @for (n of row; track n) {
      <!-- A band: a caption spanning its leaves, with no data of its own. -->
      @if (n.children.length) {
      <th
        [attr.colspan]="n.colspan"
        [style.text-align]="'center'"
        class="agrid-band"
      >
        {{ n.col.caption || n.col.dataField }}
      </th>
      } @else {
      <ng-container
        *ngTemplateOutlet="
          leafHead;
          context: { col: n.col, rowspan: n.rowspan }
        "
      ></ng-container>
      } } @if (level === 0) {
      <!-- dxo-editing action column (row mode) -->
      @if (showActionsColumn) {
      <th
        class="agrid-actions"
        nzWidth="110px"
        [attr.rowspan]="headerRows.length > 1 ? headerRows.length : null"
      >
        Actions
      </th>
      } }
    </tr>
    }

    <!-- nzWidth, NOT [style.width]: NzThMeasureDirective reads nzWidth to build
        the table's <colgroup>, and that colgroup is what NzCellFixedDirective
        measures to compute the sticky offset for a pinned column. Setting the
        width through CSS instead leaves the measurement at zero, so pinned
        columns stack on top of each other at left: 0 - visible only when
        someone scrolls a wide table sideways. -->
    <ng-template #leafHead let-col="col" let-rowspan="rowspan">
      <th
        #thRef
        [attr.rowspan]="rowspan > 1 ? rowspan : null"
        [nzWidth]="widthOf(col)"
        [style.min-width.px]="col.minWidth"
        [style.text-align]="col.alignment || 'left'"
        [nzLeft]="col.fixed === true && col.fixedPosition !== 'right'"
        [nzRight]="col.fixed === true && col.fixedPosition === 'right'"
        [class.agrid-sortable]="col.allowSorting !== false && col.dataField"
        [class.agrid-resizing]="isResizing"
      >
        <span class="agrid-headertext" (click)="onSort(col)">
          @if (headerTemplateFor(col.headerCellTemplate); as htpl) {
          <ng-container
            *ngTemplateOutlet="htpl; context: { $implicit: col, column: col }"
          ></ng-container>
          } @else {
          {{ col.caption || col.dataField }}
          } @if (sortField === col.dataField) {
          <span
            nz-icon
            [nzType]="sortDir === 'asc' ? 'caret-up' : 'caret-down'"
            class="agrid-sorticon"
          ></span>
          }
        </span>

        <!-- Header filter (dxo-header-filter): distinct values, checkable. -->
        @if (headerFilter && col.dataField && col.allowHeaderFiltering !== false
        && col.allowFiltering !== false) {
        <span
          class="agrid-hf"
          nz-dropdown
          [nzDropdownMenu]="hfMenu"
          nzTrigger="click"
          (click)="$event.stopPropagation()"
        >
          <span
            nz-icon
            nzType="filter"
            [class.agrid-hf-active]="headerFilterValues[col.dataField]?.size"
          ></span>
        </span>
        }
        <nz-dropdown-menu #hfMenu="nzDropdownMenu">
          <div class="agrid-hf-panel">
            @for (v of distinctValues(col); track v) {
            <label
              nz-checkbox
              [ngModel]="isHeaderFilterChecked(col, v)"
              (ngModelChange)="toggleHeaderFilter(col, v)"
              >{{ v === "" ? "(blank)" : v }}</label
            >
            }
          </div>
        </nz-dropdown-menu>
        <!-- allowColumnResizing. Reorder is deliberately not implemented -
            see the note in the component. -->
        @if (col.allowResizing !== false) {
        <span
          class="agrid-resizer"
          (mousedown)="startResize($event, col, thRef)"
          (click)="$event.stopPropagation()"
          title="Drag to resize column"
        ></span>
        }
      </th>
    </ng-template>

    <!-- Filter row (dxo-filter-row): one editor per column, ANDed together. -->
    @if (filterRow) {
    <tr class="agrid-filterrow">
      @if (masterDetail) {
      <th class="agrid-narrow"></th>
      } @if (selectionMode !== 'none') {
      <th class="agrid-narrow"></th>
      } @for (col of visibleColumns; track col) {
      <th>
        @if (col.dataField && col.allowFiltering !== false) {
        <input
          nz-input
          nzSize="small"
          [ngModel]="filterValues[col.dataField] || ''"
          (ngModelChange)="onFilter(col, $event)"
          [attr.aria-label]="'Filter ' + (col.caption || col.dataField)"
        />
        }
      </th>
      } @if (showActionsColumn) {
      <th class="agrid-actions"></th>
      }
    </tr>
    }
  </thead>

  <tbody>
    @for (line of table.data; track $index; let rowIdx = $index) {
    <!-- Group header row (dxo-grouping): collapsible, with a record count. -->
    @if (line.__group) {
    <tr class="agrid-grouprow" (click)="toggleGroupRow(line.key)">
      <td [attr.colspan]="spanAllColumns">
        <span
          nz-icon
          [nzType]="isCollapsed(line.key) ? 'right' : 'down'"
        ></span>
        <strong>{{ line.caption }}</strong>
        <span class="agrid-groupcount">({{ line.count }})</span>
      </td>
    </tr>
    }
    <!-- Group footer (dxi-group-item [showInGroupFooter] [alignByColumn]):
          one aggregate row per group, each value under its own column. -->
    @if (line.__groupFooter) {
    <tr class="agrid-groupfooter">
      @if (masterDetail) {
      <td class="agrid-narrow"></td>
      } @if (selectionMode !== 'none') {
      <td class="agrid-narrow"></td>
      } @for (col of visibleColumns; track col) {
      <td [style.text-align]="col.alignment || 'left'">
        @if (col.dataField && line.agg && line.agg[col.dataField] !== undefined)
        {
        <strong>
          {{ line.agg[col.dataField] }}
        </strong>
        }
      </td>
      } @if (showActionsColumn) {
      <td class="agrid-actions"></td>
      }
    </tr>
    } @if (!line.__group && !line.__groupFooter) {
    <tr
      (click)="onRowClick(line.data, rowIdx, $event)"
      [appGridRowHook]="rowPrepared"
      [rowHookData]="line.data"
      [class.agrid-rowselected]="
        selectionMode !== 'none' && isSelected(line.data)
      "
    >
      @if (masterDetail) {
      <td class="agrid-narrow">
        <span
          nz-icon
          class="agrid-expander"
          [nzType]="isExpanded(line.data) ? 'minus-square' : 'plus-square'"
          (click)="$event.stopPropagation(); toggleExpand(line.data)"
        ></span>
      </td>
      } @if (selectionMode !== 'none') {
      <td class="agrid-narrow" (click)="$event.stopPropagation()">
        <label
          nz-checkbox
          [ngModel]="isSelected(line.data)"
          [nzDisabled]="isRowSelectable ? !isRowSelectable(line.data) : false"
          (ngModelChange)="toggleRow(line.data, $event)"
          [attr.aria-label]="'Select row'"
        ></label>
      </td>
      } @for (col of visibleColumns; track col) {
      <td
        [style.text-align]="col.alignment || 'left'"
        [ngClass]="col.cssClass"
        [nzLeft]="col.fixed === true && col.fixedPosition !== 'right'"
        [nzRight]="col.fixed === true && col.fixedPosition === 'right'"
        [appGridCellHook]="cellPrepared"
        [hookRow]="line.data"
        [hookColumn]="col"
        [hookValue]="value(line.data, col)"
        (click)="onCellClick(line.data, col)"
      >
        <!-- Row under edit: swap the cell for an editor. Bound to editBuffer,
                  a COPY, so Cancel discards without having touched the record. -->
        @if (isEditing(line.data) && isColumnEditable(col)) {
        <!-- dxo-lookup columns edit through a select over the reference
                    list, not a free-text box over a foreign key. -->
        @if (col.lookup) {
        <nz-select
          nzSize="small"
          nzShowSearch
          [ngModel]="editBuffer[col.dataField]"
          (ngModelChange)="editBuffer[col.dataField] = $event"
          (click)="$event.stopPropagation()"
          style="width: 100%"
        >
          @for (o of lookupItems(col); track o) {
          <nz-option
            [nzValue]="o[col.lookup.valueExpr]"
            [nzLabel]="o[col.lookup.displayExpr]"
          ></nz-option>
          }
        </nz-select>
        } @else {
        <input
          nz-input
          nzSize="small"
          [type]="editorType(col)"
          [ngModel]="editBuffer[col.dataField]"
          (ngModelChange)="editBuffer[col.dataField] = $event"
          (click)="$event.stopPropagation()"
          [attr.aria-label]="'Edit ' + (col.caption || col.dataField)"
        />
        } } @else { @if (cellTemplateFor(col.cellTemplate); as tpl) {
        <ng-container
          *ngTemplateOutlet="
            tpl;
            context: {
              $implicit: line.data,
              row: line.data,
              data: line.data,
              value: value(line.data, col),
              column: col,
              cell: {
                data: line.data,
                key: keyOfRow(line.data),
                value: value(line.data, col),
                column: col,
                rowIndex: rowIdx,
                rowType: 'data',
                row: {
                  data: line.data,
                  key: keyOfRow(line.data),
                  rowIndex: rowIdx,
                  rowType: 'data'
                }
              }
            }
          "
        ></ng-container>
        } @else {
        {{ display(line.data, col) }}
        } }
        <!-- `cell` is DevExtreme's cell-info shape ({ data, key, value,
                    column, rowIndex, rowType, row }). `row` is NOT redundant with
                    the flat fields: DevExtreme exposed the record twice, and a
                    converted body reads it either way - `d.data.x` OR
                    `d.row.data.x`. Omitting `row` made the second form throw
                    mid-render, which aborts the update pass: the row's earlier
                    cells keep their values, everything after the failing cell
                    stays blank, and the empty-state placeholder never clears.
                    It exists so a `*dxTemplate` body ports VERBATIM:
                    117 files read `data.data.someField`, and rewriting every one of
                    those to `row.someField` is 117 chances to typo a field name into
                    a silently blank cell. Conversion becomes a one-line header swap:
                    <div *dxTemplate="let data of 'x'">   ->
                      <ng-template appGridCell="x" let-data="cell"> -->
      </td>
      }
      <!-- dxo-editing action column -->
      @if (showActionsColumn) {
      <td class="agrid-actions" (click)="$event.stopPropagation()">
        @if (!isEditing(line.data)) { @if (canUpdate) {
        <a (click)="startEdit(line.data)" nz-tooltip nzTooltipTitle="Edit">
          <span nz-icon nzType="edit"></span>
        </a>
        } @if (canDelete) {
        <a
          class="agrid-danger"
          (click)="removeRow(line.data)"
          nz-tooltip
          nzTooltipTitle="Delete"
        >
          <span nz-icon nzType="delete"></span>
        </a>
        } } @else {
        <a (click)="saveEdit()" nz-tooltip nzTooltipTitle="Save">
          <span nz-icon nzType="check"></span>
        </a>
        <a
          class="agrid-danger"
          (click)="cancelEdit()"
          nz-tooltip
          nzTooltipTitle="Cancel"
        >
          <span nz-icon nzType="close"></span>
        </a>
        }
      </td>
      }
    </tr>
    }
    <!-- Detail row (dxo-master-detail), rendered from <ng-template appGridDetail>. -->
    @if (!line.__group && !line.__groupFooter && masterDetail &&
    isExpanded(line.data) && detailTemplate) {
    <tr class="agrid-detailrow">
      <td [attr.colspan]="spanAllColumns">
        <!-- `cell` mirrors DevExtreme's master-detail context ({ data, key }),
                      so a `*dxTemplate="let entCust of 'detail'"` body reading
                      `entCust.data.assets` ports without being rewritten. -->
        <ng-container
          *ngTemplateOutlet="
            detailTemplate;
            context: {
              $implicit: line.data,
              row: line.data,
              cell: { data: line.data, key: keyOfRow(line.data) }
            }
          "
        ></ng-container>
      </td>
    </tr>
    } }
  </tbody>

  <!-- Footer summaries (dxo-summary / dxi-total-item). -->
  @if (allTotalItems.length) {
  <tfoot>
    <tr class="agrid-summary">
      @if (masterDetail) {
      <td class="agrid-narrow"></td>
      } @if (selectionMode !== 'none') {
      <td class="agrid-narrow"></td>
      } @for (col of visibleColumns; track col) {
      <td [style.text-align]="col.alignment || 'left'">
        @for (item of allTotalItems; track item) { @if ((item.showInColumn ||
        item.column) === col.dataField) {
        <strong>{{ summaryValue(item) }}</strong>
        } }
      </td>
      } @if (showActionsColumn) {
      <td class="agrid-actions"></td>
      }
    </tr>
  </tfoot>
  }
</nz-table>
